import React, { useState, useEffect } from 'react';
import './Navbar.css';
import AkinevoLogo from '../assets/images/Akinevo.png';
import { useLanguage } from '../context/LanguageContext';

const Navbar = ({ currentPage, onNavigate, themeMode, onToggleTheme }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const { lang, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    if (currentPage !== 'home') return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);

      const sections = ['hero', 'hakkinda', 'vizyon', 'hizmetler', 'uygulamalar', 'iletisim'];
      const scrollPosition = window.scrollY + 220;

      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    setMenuOpen(false);

    if (currentPage !== 'home') {
      if (onNavigate) onNavigate('home', targetId);
    } else {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const toggleThemeMode = () => {
    const nextMode = themeMode === 'modern' ? 'classic' : 'modern';
    onToggleTheme(nextMode);
  };

  return (
    <header className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="logo-container">
          <a href="https://akinevo.com" className="logo-link">
            <img src={AkinevoLogo} alt="Akinevo Logo" className="logo-img" />
          </a>
        </div>

        {/* Navigation Links */}
        <nav className={`nav-wrapper ${menuOpen ? 'open' : ''}`}>
          <ul className="nav-list">
            <li>
              <a
                href="#hero"
                className={`nav-link-item ${currentPage === 'home' && activeSection === 'hero' ? 'active-link' : ''}`}
                onClick={(e) => handleNavClick(e, 'hero')}
              >
                <i className="fa-solid fa-house-chimney nav-icon"></i>
                <span>{t('nav.home')}</span>
              </a>
            </li>
            <li>
              <a
                href="#hakkinda"
                className={`nav-link-item ${currentPage === 'home' && activeSection === 'hakkinda' ? 'active-link' : ''}`}
                onClick={(e) => handleNavClick(e, 'hakkinda')}
              >
                <i className="fa-solid fa-circle-info nav-icon"></i>
                <span>{t('nav.about')}</span>
              </a>
            </li>
            <li>
              <a
                href="#hizmetler"
                className={`nav-link-item ${currentPage === 'home' && activeSection === 'hizmetler' ? 'active-link' : ''}`}
                onClick={(e) => handleNavClick(e, 'hizmetler')}
              >
                <i className="fa-solid fa-cube nav-icon"></i>
                <span>{t('nav.services')}</span>
              </a>
            </li>
            <li>
              <a
                href="#uygulamalar"
                className={`nav-link-item ${currentPage === 'home' && activeSection === 'uygulamalar' ? 'active-link' : ''}`}
                onClick={(e) => handleNavClick(e, 'uygulamalar')}
              >
                <i className="fa-solid fa-layer-group nav-icon"></i>
                <span>{t('nav.apps')}</span>
              </a>
            </li>
            <li>
              <button
                type="button"
                className={`nav-link-item ${currentPage === 'testimonials' ? 'active-link' : ''}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
                onClick={() => {
                  setMenuOpen(false);
                  if (onNavigate) onNavigate('testimonials');
                }}
              >
                <i className="fa-solid fa-comment-dots nav-icon"></i>
                <span>{t('nav.testimonials')}</span>
              </button>
            </li>
            <li className="mobile-only-nav-item">
              <a
                href="#iletisim"
                className={`nav-link-item nav-mobile-contact-btn ${currentPage === 'home' && activeSection === 'iletisim' ? 'active-link' : ''}`}
                onClick={(e) => handleNavClick(e, 'iletisim')}
              >
                <i className="fa-solid fa-paper-plane nav-icon"></i>
                <span>{t('nav.contact')}</span>
              </a>
            </li>
          </ul>
        </nav>

        {/* Right Actions: Single-Click Theme Toggle, Lang Toggle & Contact CTA */}
        <div className="nav-actions">
          {/* Dil Değiştirici Buton */}
          <button
            type="button"
            className="lang-pill-btn"
            onClick={toggleLanguage}
            title={lang === 'tr' ? "Switch to English" : "Türkçe'ye Geç"}
            aria-label="Dili Değiştir"
          >
            <i className="fa-solid fa-globe"></i>
            <span className="lang-text">{lang === 'tr' ? 'EN' : 'TR'}</span>
          </button>

          <button
            type="button"
            className="theme-mode-pill-btn"
            onClick={toggleThemeMode}
            title={themeMode === 'modern' ? 'Klasik Kurumsal Görünüme Geç' : 'Neo Modern Görünüme Geç'}
            aria-label="Görünüm Modunu Değiştir"
          >
            <div className={`mode-icon-circle ${themeMode}`}>
              {themeMode === 'modern' ? (
                <i className="fa-solid fa-wand-magic-sparkles"></i>
              ) : (
                <i className="fa-solid fa-briefcase"></i>
              )}
            </div>
            <span className="mode-label">
              {themeMode === 'modern' ? t('nav.modeNeo') : t('nav.modeClassic')}
            </span>
          </button>

          <a
            href="#iletisim"
            className={`contact-nav-pill ${currentPage === 'home' && activeSection === 'iletisim' ? 'active-link' : ''}`}
            onClick={(e) => handleNavClick(e, 'iletisim')}
          >
            <span>{t('nav.contact')}</span>
            <i className="fa-solid fa-arrow-right-long contact-arrow"></i>
          </a>

          {/* Mobile Hamburger Toggle */}
          <button
            className={`menu-toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menüyü Aç/Kapat"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
